<template>
  <div>
    <page-header
      :page="pageConfig"
      :actions="pageActionsConfig"
      :has-return="true"
    />
    <progress-filter
      :academic-years="academicYears"
      :default-value="lastAcademicYear"
      @on-change-academic-year="onChangeAcademicYearHandler"
    />
    <section-title
      class="mb-3 pl-1 ml-1 section-title__extended"
      icon="fas fa-clipboard-list-check"
      title="Resumen de Información General"
      title-class="font-16"
    />
    <general-info-summary
      :total-count="summary.totalCount"
      :pending-count="summary.pendingCount"
      :completed-count="summary.completedPercentage"
      class="ml-2"
    />
    <div class="ml-2 mt-3">
      <summary-collapse
        :is-opened="true"
        :action-summary="actionSummary"
        title="Detalle de Resumen Actual"
      >
        <progress-info-summary :items="currentProgressSummary" />
      </summary-collapse>
    </div>
    <div class="mt-4 ml-2">
      <section-title has-line title="Historial de Informes de Progreso" />
      <progress-info-history
        :items="progressHistory"
        :action-items="actionItems"
        @on-refresh="onRefreshHandler"
        @on-close-ip="onCloseIpHandler"
        @on-update-ip="onUpdateIpHandler"
        @on-open-detail="onOpenDetailHandler"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import {
  GeneralInfoSummary,
  ProgressFilter,
  ProgressInfoHistory,
  ProgressInfoSummary,
  SummaryCollapse,
} from '../shared/components';
import Service from '@/services/api/progress.api';
import DocumentServices from '@/modules/report/document';
import stipulationType from '@/utils/constants/stipulationType.js';

export default {
  components: {
    ProgressFilter,
    PageHeader,
    SectionTitle,
    GeneralInfoSummary,
    SummaryCollapse,
    ProgressInfoSummary,
    ProgressInfoHistory,
  },
  data: () => ({
    academicYears: [],
    selectedAcademicYear: null,
    summary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    progressHistory: [],
    currentProgressSummary: [],
  }),
  computed: {
    stipulationType() {
      return stipulationType.ST43;
    },
    pageConfig() {
      return {
        title: 'Informes de Progreso: Estipulación 43',
        icon: 'fas fa-calculator',
        routes: ['Inicio', 'Informes de Progreso', 'Estipulación 43'],
      };
    },
    pageActionsConfig() {
      return [
        {
          text: 'Actualizar IP',
          icon: 'fas fa-sync-alt pr-2',
          variant: 'success',
          visible: true,
          click: () => this.onUpdateProgressReportHandler(),
        },
      ];
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadHandler(item),
          },
        ];
      };
    },
    actionSummary() {
      return () => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadSummaryHandler(),
          },
        ];
      };
    },
    lastAcademicYear() {
      if (!this.academicYears?.length) {
        return null;
      }

      return this.academicYears[0];
    },
    isLastAcademicYearSelected() {
      if (!this.academicYears?.length) {
        return false;
      }

      return this.lastAcademicYear?.id === this.selectedAcademicYear?.id;
    },
    isLastIpClosed() {
      return !this.progressHistory.length
        ? true
        : this.progressHistory[0].isClosed;
    },
  },
  async mounted() {
    await this.getInit();
  },
  methods: {
    async getInit() {
      await this.getAvailableAcademicYears();
      if (this.academicYears?.length) {
        this.selectedAcademicYear = this.lastAcademicYear;
        await this.getDetail();
      }
    },
    async getAvailableAcademicYears() {
      this.academicYears = (
        await Service.getAcademicYears(this.stipulationType)
      ).data;
    },
    async getDetail() {
      const response = (
        await Service.getDetail({
          stipulationType: this.stipulationType,
          academicYearId: this.selectedAcademicYear.id,
        })
      ).data;
      if (response) {
        this.summary = response?.summaries[0];
        this.currentProgressSummary = response.currentSummaryDetail;
        this.progressHistory = response.progressHistory;
      }
    },
    async onRefreshHandler() {
      await this.getDetail();
    },
    onChangeAcademicYearHandler(academicYear) {
      this.selectedAcademicYear = academicYear;
      this.getDetail();
    },
    onCloseIpHandler({ summaryId }) {
      if (summaryId) {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea congelar los datos y cerrar un IP?',
          'Sí',
          'No'
        ).then(async (res) => {
          if (res) {
            const result = (
              await Service.closeIp(this.stipulationType, summaryId)
            ).data;
            if (result) {
              this.ShowToast(
                'Proceso Exitoso',
                'El IP ha sido cerrado exitosamente.',
                'success'
              );
              this.onRefreshHandler();
            }
          }
        });
      }
    },
    onUpdateProgressReportHandler() {
      if (!this.isLastIpClosed) {
        this.ShowToast(
          'Alerta',
          'Para crear una nueva actualización es necesario cerrar el último IP.',
          'error'
        );
      } else {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea actualizar los datos y generar un IP?',
          'Sí',
          'No'
        ).then((res) => {
          if (res) {
            Service.updateIp(
              this.stipulationType,
              this.selectedAcademicYear.id,
              false
            ).then((res) => {
              if (res.data) {
                this.ShowToast(
                  'Proceso Exitoso',
                  'El IP ha sido actualizado exitosamente.',
                  'success'
                );
                this.onRefreshHandler();
              } else {
                this.ShowToast(
                  'Alerta',
                  'No es posible generar un IP, ya que no cuenta con datos para actualizar en sistema.',
                  'error'
                );
              }
            });
          }
        });
      }
    },
    onUpdateIpHandler() {
      Service.updateIp(
        this.stipulationType,
        this.selectedAcademicYear.id,
        true
      ).then((res) => {
        if (res.data) {
          this.ShowToast(
            'Proceso Exitoso',
            `El IP ha sido actualizado exitosamente.`,
            'success'
          );
          this.onRefreshHandler();
        } else {
          this.ShowToast(
            'Alerta',
            'No cuenta con datos para actualizar en sistema.',
            'error'
          );
        }
      });
    },
    async onDownloadHandler(item) {
      await Service.downloadIpStipulation({
        stipulationType: this.stipulationType,
        ipSequence: item.ipSequence,
        academicYearId: item.academicYearId,
      })
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    async onDownloadSummaryHandler() {
      if (this.selectedAcademicYear) {
        var academicYearId = this.selectedAcademicYear.id;
        this.onDownloadHandler({ academicYearId, ipSequence: 0 });
      }
    },
    async onOpenDetailHandler(index) {
      this.progressHistory = await Promise.all(
        this.progressHistory.map(async (item, itemIndex) => {
          if (itemIndex === index) {
            item.isOpen = index === itemIndex ? !item.isOpen : false;
            if (item.isOpen && !item.summary) {
              item.summary = (
                await Service.getIpHistory(
                  this.stipulationType,
                  this.selectedAcademicYear.id,
                  item.ipSequence
                )
              ).data;
            }
          }
          return item;
        })
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/views/progress/shared/scss/common.scss';
</style>
